import {
  ChevronRightIcon,
  CrossIcon,
  DropdownMenu,
  MenuIcon,
} from '@setel/portal-ui';
import { useSearchParams } from 'hooks/router.hooks';
import * as React from 'react';
import { LOCAL_STORAGE_KEY } from 'shared/consts/auth.const';
import { Tooltip } from './tooltip';
import {
  getLocalStorageItem,
  setLocalStorageItem,
} from 'modules/auth/auth.service';

export enum NavigationDropdownSearchQueryValue {
  USER_ATTRIBUTES = 'userAttributes',
  LOYALTY = 'loyaltyDetails',
  FUEL_ORDER = 'fuelOrder',
  STORE_ORDER = 'storeOrder',
  TOPUP_AND_PAYMENTS = 'topupNPayment',
  USER_PROFILE = 'userProfile',
  ENGAGEMENT = 'engagement',
}
export type NavigationDropdownSearchQueryValueType =
  | 'userAttributes'
  | 'loyaltyDetails'
  | 'fuelOrder'
  | 'storeOrder'
  | 'topupNPayment'
  | 'userProfile'
  | 'engagement';
export const NavigationDropdownSearchQueryKey = 'viewOptions';

export enum NavigationLabel {
  USER_ATTRIBUTES = 'User Attributes',
  LOYALTY = 'Loyalty details',
  FUEL_ORDER = 'Fuel order',
  STORE_ORDER = 'Store order',
  TOPUP_AND_PAYMENTS = 'Payment transactions',
  USER_PROFILE = 'User profile',
  ENGAGEMENT = 'Engagement',
}

const MapSearchQueryToLabel: Record<
  NavigationDropdownSearchQueryValue,
  NavigationLabel
> = {
  [NavigationDropdownSearchQueryValue.USER_ATTRIBUTES]:
    NavigationLabel.USER_ATTRIBUTES,
  [NavigationDropdownSearchQueryValue.LOYALTY]: NavigationLabel.LOYALTY,
  [NavigationDropdownSearchQueryValue.FUEL_ORDER]: NavigationLabel.FUEL_ORDER,
  [NavigationDropdownSearchQueryValue.STORE_ORDER]: NavigationLabel.STORE_ORDER,
  [NavigationDropdownSearchQueryValue.TOPUP_AND_PAYMENTS]:
    NavigationLabel.TOPUP_AND_PAYMENTS,
  [NavigationDropdownSearchQueryValue.USER_PROFILE]:
    NavigationLabel.USER_PROFILE,
  [NavigationDropdownSearchQueryValue.ENGAGEMENT]: NavigationLabel.ENGAGEMENT,
};

export const NavigationDropdown = () => {
  const { searchParams, onUpdateSearchParam } = useSearchParams();
  const [selectedLabel, setSelectedLabel] = React.useState<NavigationLabel>(
    MapSearchQueryToLabel[
      searchParams.get(
        NavigationDropdownSearchQueryKey
      ) as NavigationDropdownSearchQueryValue
    ] || NavigationLabel.USER_ATTRIBUTES
  );

  function noop() {}

  const [isTooltipShown, setTooltipShown] = React.useState(
    getLocalStorageItem(LOCAL_STORAGE_KEY.navigationDropdownTooltip, false) as
      | null
      | string
  );

  const [mouseEnterTimeStamp, setMouseEnterTimeStamp] = React.useState(0);

  React.useEffect(() => {
    setSelectedLabel(
      MapSearchQueryToLabel[
        searchParams.get(
          NavigationDropdownSearchQueryKey
        ) as NavigationDropdownSearchQueryValue
      ] || NavigationLabel.USER_ATTRIBUTES
    );
  }, [searchParams]);

  const onSelectViewOptions = (
    value: NavigationDropdownSearchQueryValueType
  ) => {
    onUpdateSearchParam({ [NavigationDropdownSearchQueryKey]: value });
  };

  function setTooltipAsShown() {
    const currentTime = new Date().toISOString();
    setTooltipShown(currentTime);
    setLocalStorageItem(
      LOCAL_STORAGE_KEY.navigationDropdownTooltip,
      currentTime
    );
  }

  return (
    <div
      className="text-lg py-5 inline group relative"
      onMouseEnter={
        isTooltipShown
          ? noop
          : (e) => {
              setMouseEnterTimeStamp(e.timeStamp);
            }
      }
      onMouseLeave={
        isTooltipShown
          ? noop
          : (e) => {
              if (
                mouseEnterTimeStamp &&
                !isTooltipShown &&
                e.timeStamp - mouseEnterTimeStamp > 1000
              ) {
                setTooltipAsShown();
              }
            }
      }
      onClick={() => {
        if (!isTooltipShown) {
          setTooltipAsShown();
        }
      }}
    >
      <DropdownMenu
        value={selectedLabel}
        label={({ isExpanded }) => (
          <div className="inline-flex justify-between w-full align-middle py-5">
            <div className="inline-flex">
              {isExpanded ? (
                <CrossIcon className="w-5 h-5 mr-4 my-auto" />
              ) : (
                <MenuIcon className="w-5 h-5 mr-4 my-auto" />
              )}
              <div className="text-lg font-medium">{selectedLabel}</div>
            </div>
            {
              <ChevronRightIcon
                className={`transform ${
                  isExpanded ? `-rotate-90` : `rotate-90`
                } justify-self-end my-auto`}
              />
            }
          </div>
        )}
        rightIcon="null"
        className="w-full justify-start rounded-none h-auto shadow-none border border-t-0"
        data-testid="navigation-dropdown"
      >
        {!isTooltipShown && (
          <Tooltip message="Click here to navigate to other feature." />
        )}
        <DropdownMenu.Items width="match-button" className="text-lg">
          {Object.entries(MapSearchQueryToLabel).map(
            ([value, displayValue]) => (
              <DropdownMenu.Item
                className={
                  selectedLabel === displayValue
                    ? 'font-semibold pl-12 ml-1'
                    : 'pl-12 ml-1'
                }
                onSelect={() =>
                  onSelectViewOptions(
                    value as NavigationDropdownSearchQueryValueType
                  )
                }
                key={displayValue}
              >
                {displayValue}
              </DropdownMenu.Item>
            )
          )}
        </DropdownMenu.Items>
      </DropdownMenu>
    </div>
  );
};
